import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import apiService from '../../services/api.service';
import './SecurityReport.scss';

const TYPE_LABELS = {
    login_success: { label: 'Connexion réussie', className: 'success', icon: 'ph-sign-in' },
    login_failed: { label: 'Échec de connexion', className: 'warning', icon: 'ph-warning' },
    login_locked: { label: 'Connexion bloquée', className: 'danger', icon: 'ph-lock' },
    bot_detected: { label: 'Robot détecté', className: 'danger', icon: 'ph-robot' },
    attack_detected: { label: 'Tentative d\'injection', className: 'danger', icon: 'ph-bug' },
    scanner_blocked: { label: 'Scan / piratage', className: 'danger', icon: 'ph-detective' },
    auto_blocked: { label: 'IP bloquée auto', className: 'dark', icon: 'ph-prohibit' },
};

const VERDICT_LABELS = {
    bot: { label: 'Robot', className: 'bot', icon: 'ph-robot' },
    suspect: { label: 'Suspect', className: 'suspect', icon: 'ph-question' },
    human: { label: 'Humain', className: 'human', icon: 'ph-user' },
};

const CATEGORY_LABELS = {
    sql: 'Injection SQL',
    xss: 'Injection JavaScript (XSS)',
    traversal: 'Accès aux fichiers du serveur',
    command: 'Injection de commande',
};

const FILTERS = [
    { key: 'all', label: 'Tout' },
    { key: 'login_failed', label: 'Échecs' },
    { key: 'login_success', label: 'Réussies' },
    { key: 'bot_detected', label: 'Robots' },
    { key: 'attack_detected', label: 'Injections' },
    { key: 'scanner_blocked', label: 'Scans' },
    { key: 'login_locked', label: 'Bloquées' },
    { key: 'auto_blocked', label: 'Auto-blocage' },
];

const formatDate = (value) => new Date(value).toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

const SecurityReport = () => {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [topIps, setTopIps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [verdict, setVerdict] = useState('');
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [expanded, setExpanded] = useState(null);
    const [ipBusy, setIpBusy] = useState(false);

    const fetchStats = useCallback(async () => {
        try {
            const response = await apiService.get('/security-logs/stats');
            setStats(response.stats);
            setTopIps(response.top_ips || []);
        } catch (error) {
            console.error('Error fetching security stats:', error);
        }
    }, []);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { type: filter, page, per_page: 25 };
            if (verdict) params.verdict = verdict;
            if (appliedSearch) params.search = appliedSearch;
            const response = await apiService.get('/security-logs', params);
            setLogs(response.logs.data || []);
            setLastPage(response.logs.last_page || 1);
        } catch (error) {
            console.error('Error fetching security logs:', error);
        } finally {
            setLoading(false);
        }
    }, [filter, verdict, page, appliedSearch]);

    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const refresh = () => {
        fetchStats();
        fetchLogs();
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setAppliedSearch(search.trim());
    };

    const toggleBlock = async (ip, isBlocked) => {
        if (!ip) return;
        const question = isBlocked
            ? `Débloquer l'adresse IP ${ip} ?`
            : `Bloquer l'adresse IP ${ip} ?\nCette IP n'aura plus accès au site.`;
        if (!window.confirm(question)) return;

        setIpBusy(true);
        try {
            if (isBlocked) {
                await apiService.delete(`/blocked-ips/${encodeURIComponent(ip)}`);
            } else {
                await apiService.post('/blocked-ips', { ip_address: ip, reason: 'Rapport sécurité' });
            }
            refresh();
        } catch (error) {
            alert(error.message || 'Erreur');
        } finally {
            setIpBusy(false);
        }
    };

    const renderIpButton = (ip, isBlocked) => ip && (
        <button
            className={`ip-toggle ${isBlocked ? 'unblock' : 'block'}`}
            onClick={(e) => { e.stopPropagation(); toggleBlock(ip, isBlocked); }}
            disabled={ipBusy}
        >
            <i className={`ph ${isBlocked ? 'ph-lock-open' : 'ph-prohibit'}`}></i>
            {isBlocked ? 'Débloquer' : 'Bloquer'}
        </button>
    );

    const renderDetails = (log) => {
        const d = log.details || {};
        const pwd = d.password;
        const signals = d.signals ? Object.values(d.signals) : [];

        return (
            <div className="log-details">
                {d.reason && <div><strong>Raison :</strong> {d.reason}</div>}
                {d.category && <div><strong>Type d'attaque :</strong> {CATEGORY_LABELS[d.category] || d.category} (champ « {d.field} »)</div>}
                {d.tool && <div><strong>Outil :</strong> {d.tool}</div>}
                {d.role && <div><strong>Rôle :</strong> {d.role}</div>}
                {d.attempts && <div><strong>Tentatives :</strong> {d.attempts}</div>}
                {pwd && (
                    <div>
                        <strong>Mot de passe saisi :</strong>{' '}
                        {pwd.common_value
                            ? <><code>{pwd.common_value}</code> <span className="hint danger">mot de passe de dictionnaire (typique des robots)</span></>
                            : <>{'•'.repeat(Math.min(pwd.length || 0, 16))} <span className="hint">({pwd.length || 0} caractères{pwd.composition ? ` : ${pwd.composition}` : ''})</span></>}
                    </div>
                )}
                {d.form_time_ms !== undefined && <div><strong>Temps de saisie :</strong> {(d.form_time_ms / 1000).toFixed(1)} s</div>}
                {signals.length > 0 && (
                    <div>
                        <strong>Indices :</strong>
                        <ul>{signals.map((s) => <li key={s}>{s}</li>)}</ul>
                    </div>
                )}
                {log.payload && (
                    <div>
                        <strong>Contenu saisi :</strong>
                        <pre>{log.payload}</pre>
                    </div>
                )}
                {log.url && <div className="break"><strong>URL :</strong> <code>{log.method} {log.url}</code></div>}
                {log.user_agent && <div className="break"><strong>Navigateur :</strong> {log.user_agent}</div>}
            </div>
        );
    };

    const statCards = stats ? [
        { key: 'login_success', label: 'Connexions réussies', value: stats.login_success, icon: 'ph-sign-in', tone: 'success' },
        { key: 'login_failed', label: 'Échecs de connexion', value: stats.login_failed, icon: 'ph-warning', tone: 'warning' },
        { key: 'bots', label: 'Robots détectés', value: stats.bots, icon: 'ph-robot', tone: 'danger' },
        { key: 'attacks', label: 'Attaques bloquées', value: stats.attacks, icon: 'ph-shield-warning', tone: 'danger' },
        { key: 'blocked', label: 'IPs bloquées', value: stats.blocked_ips, icon: 'ph-prohibit', tone: 'dark' },
    ] : [];

    return (
        <div className="security-report-page">
            <div className="page-header">
                <div>
                    <h1>Rapport Sécurité</h1>
                    <p>Tentatives de connexion, robots et attaques détectés (dernières 24 h pour les compteurs)</p>
                </div>
                <button className="refresh-btn" onClick={refresh}>
                    <i className="ph ph-arrows-clockwise"></i> Actualiser
                </button>
            </div>

            <div className="stats-grid">
                {statCards.map((card) => (
                    <div key={card.key} className={`stat-card ${card.tone}`}>
                        <i className={`ph ${card.icon}`}></i>
                        <div>
                            <span className="stat-value">{card.value}</span>
                            <span className="stat-label">{card.label}</span>
                        </div>
                    </div>
                ))}
            </div>

            {topIps.length > 0 && (
                <div className="panel">
                    <h3>IPs les plus suspectes (7 jours)</h3>
                    <ul className="top-ips">
                        {topIps.map((row) => (
                            <li key={row.ip_address}>
                                <code>{row.ip_address}</code>
                                <span>{row.total} événement(s) · dernier : {formatDate(row.last_seen)}</span>
                                {row.is_ip_blocked && <span className="tag-blocked">Bloquée</span>}
                                {renderIpButton(row.ip_address, row.is_ip_blocked)}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="panel">
                <div className="toolbar">
                    <div className="filter-tabs">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                className={`filter-tab ${filter === f.key ? 'active' : ''}`}
                                onClick={() => { setFilter(f.key); setPage(1); }}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <div className="toolbar-right">
                        <select value={verdict} onChange={(e) => { setVerdict(e.target.value); setPage(1); }}>
                            <option value="">Tous les verdicts</option>
                            <option value="bot">Robots</option>
                            <option value="suspect">Suspects</option>
                            <option value="human">Humains</option>
                        </select>
                        <form onSubmit={handleSearch} className="search-form">
                            <i className="ph ph-magnifying-glass"></i>
                            <input
                                type="text"
                                placeholder="IP, email, URL..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </form>
                    </div>
                </div>

                {loading ? (
                    <div className="state"><Spinner animation="border" variant="primary" /></div>
                ) : logs.length === 0 ? (
                    <div className="state">
                        <i className="ph ph-shield-check"></i>
                        <p>Aucun événement.</p>
                    </div>
                ) : (
                    <div className="log-list">
                        {logs.map((log) => {
                            const type = TYPE_LABELS[log.type] || { label: log.type, className: 'dark', icon: 'ph-info' };
                            const v = VERDICT_LABELS[log.verdict];
                            const isOpen = expanded === log.id;
                            return (
                                <div key={log.id} className={`log-item ${isOpen ? 'open' : ''}`}>
                                    <div className="log-row" onClick={() => setExpanded(isOpen ? null : log.id)}>
                                        <span className={`type-badge ${type.className}`}>
                                            <i className={`ph ${type.icon}`}></i> {type.label}
                                        </span>
                                        {v && (
                                            <span className={`verdict-badge ${v.className}`}>
                                                <i className={`ph ${v.icon}`}></i> {v.label}
                                            </span>
                                        )}
                                        <span className="log-main">
                                            {log.email && <span className="log-email">{log.email}</span>}
                                            <span className="log-sub">{log.details?.reason || log.url}</span>
                                        </span>
                                        <span className="log-ip">
                                            <code>{log.ip_address}</code>
                                            {log.is_ip_blocked && <span className="tag-blocked">Bloquée</span>}
                                        </span>
                                        <span className="log-date">{formatDate(log.created_at)}</span>
                                        {renderIpButton(log.ip_address, log.is_ip_blocked)}
                                        <i className={`ph ph-caret-${isOpen ? 'up' : 'down'} caret`}></i>
                                    </div>
                                    {isOpen && renderDetails(log)}
                                </div>
                            );
                        })}
                    </div>
                )}

                {lastPage > 1 && (
                    <div className="pagination-bar">
                        <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                            <i className="ph ph-caret-left"></i> Précédent
                        </button>
                        <span>Page {page} sur {lastPage}</span>
                        <button disabled={page === lastPage} onClick={() => setPage(page + 1)}>
                            Suivant <i className="ph ph-caret-right"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SecurityReport;
